import { getAuthTokensFromLocalStorage, refreshAuthTokens } from './AuthApi'
import { getApiEnvironment } from './environment'

function hasJsonContentType(res: Response) {
  const contentType = res.headers.get('content-type')
  const contentIsJson = contentType != null && contentType.startsWith('application/json')
  return contentIsJson
}

/**
 * Makes a request and retries once with a refreshed CSRF token if needed
 * @param requestOptions The HTTP request options
 * @param url The endpoint URL
 * @param body The request body
 * @param retryCSRF Whether to retry the request with a refreshed CSRF token
 * @typedef T the response type
 * @typedef B Body of the request
 * @returns Promise
 */
async function request<T>(requestOptions: RequestInit, url: string, retryCSRF: boolean = true): Promise<T> {
  const { csrfToken } = getAuthTokensFromLocalStorage()

  const options: RequestInit = {
    method: requestOptions.method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
      ...requestOptions.headers,
    },
    body: requestOptions.body,
  }
  const response = await fetch(`${getApiEnvironment()}/${url}`, options)
  if (response.status === 418 && retryCSRF) {
    await refreshAuthTokens()
    return request<T>(requestOptions, url, false) // retryCSRF = false to retry CSRF only once
  }

  if (!response.ok) {
    return response.json().then(json => Promise.reject(json))
  }
  if (!hasJsonContentType(response)) {
    return response.text() as T
  }
  return response.json()
}

function APIGet<T>(url: string): Promise<T> {
  return request<T>({ method: 'GET', body: null }, url)
}

function APIPost<T, B>(url: string, data?: B): Promise<T> {
  return request<T>({ method: 'POST', body: data ? JSON.stringify(data) : null }, url)
}

function APIPut<T, B>(url: string, data?: B): Promise<T> {
  return request<T>({ method: 'PUT', body: data ? JSON.stringify(data) : null }, url)
}

function APIDelete<T>(url: string): Promise<T> {
  return request<T>({ method: 'DELETE', body: null }, url)
}

function APIPatch<T, B>(url: string, data?: B): Promise<T> {
  return request<T>({ method: 'PATCH', body: data ? JSON.stringify(data) : null }, url)
}

export { APIGet, APIPost, APIPut, APIDelete, APIPatch }
